import { useEffect, useMemo, useState } from 'react';
import {
  ActionIcon,
  Button,
  Group,
  Loader,
  Modal,
  Pagination,
  Paper,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { useDebounceValue } from 'usehooks-ts';
import { Icon } from '@/components/Icon';
import {
  useChurchGroupListAdmins,
  useRemoveChurchGroupListAdminGroupMutation,
  useRemoveChurchGroupListAdminListMutation,
} from '@/hooks/churches';
import { showError } from '@/shared';
import { GROUP_LIST_ADMIN_SORT_BY, SORT_ORDER, type ChurchGroupListAdmin } from '@/types';
import { useParams } from 'react-router-dom';
import { RemovableScopeChip } from './RemovableScopeChip';
import styles from './GroupAdmins.module.sass';

const LAYOUT = {
  PAGE_SIZE: 20,
  SORT_ICON_SIZE: 16,
  SEARCH_DEBOUNCE_MS: 400,
};

enum GROUP_LIST_ADMIN_SCOPE_TYPE {
  GROUP = 'group',
  LIST = 'list',
}

type PendingRemoval = {
  userId: string;
  userName: string;
  scopeType: GROUP_LIST_ADMIN_SCOPE_TYPE;
  scopeId: string;
  scopeName: string;
};

const getAdminDisplayName = (admin: ChurchGroupListAdmin) => {
  const fullName = `${admin.firstName || ''} ${admin.lastName || ''}`.trim();
  return fullName || admin.email || 'N/A';
};

const getRemovingKey = (userId: string, scopeType: GROUP_LIST_ADMIN_SCOPE_TYPE, scopeId: string) =>
  `${userId}:${scopeType}:${scopeId}`;

export const GroupAdmins = () => {
  const { id: churchId } = useParams<{ id: string }>();
  const { data: admins = [], isLoading: isAdminsLoading } = useChurchGroupListAdmins(churchId);
  const removeGroupMutation = useRemoveChurchGroupListAdminGroupMutation(churchId ?? '');
  const removeListMutation = useRemoveChurchGroupListAdminListMutation(churchId ?? '');

  const [termInput, setTermInput] = useState<string>('');
  const [debouncedTerm] = useDebounceValue(termInput, LAYOUT.SEARCH_DEBOUNCE_MS);
  const [sortBy, setSortBy] = useState<GROUP_LIST_ADMIN_SORT_BY>(GROUP_LIST_ADMIN_SORT_BY.NAME);
  const [sortOrder, setSortOrder] = useState<SORT_ORDER>(SORT_ORDER.ASC);
  const [page, setPage] = useState<number>(1);
  const [pendingRemoval, setPendingRemoval] = useState<PendingRemoval | null>(null);
  const [removingKey, setRemovingKey] = useState<string>('');

  useEffect(() => {
    setPage(1);
  }, [debouncedTerm, sortBy, sortOrder]);

  const filteredAdmins = useMemo(() => {
    const normalizedTerm = debouncedTerm.trim().toLowerCase();
    if (!normalizedTerm) {
      return admins;
    }

    return admins.filter((admin) => {
      const name = getAdminDisplayName(admin).toLowerCase();
      const email = (admin.email || '').toLowerCase();
      return name.includes(normalizedTerm) || email.includes(normalizedTerm);
    });
  }, [admins, debouncedTerm]);

  const sortedAdmins = useMemo(() => {
    const sorted = [...filteredAdmins];
    sorted.sort((a, b) => {
      let comparison = 0;
      if (sortBy === GROUP_LIST_ADMIN_SORT_BY.EMAIL) {
        comparison = (a.email || '').localeCompare(b.email || '', undefined, { sensitivity: 'base' });
      } else {
        const nameA = getAdminDisplayName(a);
        const nameB = getAdminDisplayName(b);
        comparison = nameA.localeCompare(nameB, undefined, { sensitivity: 'base' });
      }
      return sortOrder === SORT_ORDER.ASC ? comparison : -comparison;
    });
    return sorted;
  }, [filteredAdmins, sortBy, sortOrder]);

  const totalPages = Math.max(1, Math.ceil(sortedAdmins.length / LAYOUT.PAGE_SIZE));

  useEffect(() => {
    if (page > totalPages) {
      setPage(totalPages);
    }
  }, [page, totalPages]);

  const paginatedAdmins = useMemo(() => {
    const start = (page - 1) * LAYOUT.PAGE_SIZE;
    return sortedAdmins.slice(start, start + LAYOUT.PAGE_SIZE);
  }, [sortedAdmins, page]);

  const handleSort = (column: GROUP_LIST_ADMIN_SORT_BY) => {
    if (sortBy === column) {
      setSortOrder((prev) => (prev === SORT_ORDER.ASC ? SORT_ORDER.DESC : SORT_ORDER.ASC));
      return;
    }
    setSortBy(column);
    setSortOrder(SORT_ORDER.ASC);
  };

  const handleRemoveGroupClick = (admin: ChurchGroupListAdmin, groupId: string, groupName: string) => {
    setPendingRemoval({
      userId: admin.userId,
      userName: getAdminDisplayName(admin),
      scopeType: GROUP_LIST_ADMIN_SCOPE_TYPE.GROUP,
      scopeId: groupId,
      scopeName: groupName,
    });
  };

  const handleRemoveListClick = (admin: ChurchGroupListAdmin, listId: string, listName: string) => {
    setPendingRemoval({
      userId: admin.userId,
      userName: getAdminDisplayName(admin),
      scopeType: GROUP_LIST_ADMIN_SCOPE_TYPE.LIST,
      scopeId: listId,
      scopeName: listName,
    });
  };

  const handleConfirmRemoval = async () => {
    if (!pendingRemoval || !churchId) {
      return;
    }

    const key = getRemovingKey(pendingRemoval.userId, pendingRemoval.scopeType, pendingRemoval.scopeId);
    setRemovingKey(key);

    try {
      if (pendingRemoval.scopeType === GROUP_LIST_ADMIN_SCOPE_TYPE.GROUP) {
        await removeGroupMutation.mutateAsync({
          userId: pendingRemoval.userId,
          groupId: pendingRemoval.scopeId,
        });
      } else {
        await removeListMutation.mutateAsync({
          userId: pendingRemoval.userId,
          listId: pendingRemoval.scopeId,
        });
      }
      setPendingRemoval(null);
    } catch (error) {
      showError(error);
    } finally {
      setRemovingKey('');
    }
  };

  const getSortIconName = (column: GROUP_LIST_ADMIN_SORT_BY) => {
    if (sortBy !== column) {
      return 'IconSelector';
    }
    return sortOrder === SORT_ORDER.ASC ? 'IconArrowUp' : 'IconArrowDown';
  };

  const scopeTypeLabel =
    pendingRemoval?.scopeType === GROUP_LIST_ADMIN_SCOPE_TYPE.LIST ? 'list' : 'group';

  return (
    <Paper p="xl" radius="md" w="100%">
      <Stack gap="lg">
        <Group justify="space-between" align="center" className={styles.headerRow}>
          <Title order={3}>Group &amp; List Admins</Title>
          <Text className={styles.adminCount}>
            {isAdminsLoading ? '...' : `${filteredAdmins.length} Admins`}
          </Text>
        </Group>

        <TextInput
          placeholder="Search by name or email"
          value={termInput}
          onChange={(event) => setTermInput(event.currentTarget.value)}
          leftSection={<Icon name="IconSearch" size={16} />}
        />

        {isAdminsLoading ? (
          <Stack align="center" py="xl">
            <Loader size="md" />
            <Text c="dimmed">Loading group &amp; list admins...</Text>
          </Stack>
        ) : (
          <>
            <Table>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>
                    <Group gap={4} className={styles.sortableHeader}>
                      <Text fw={700} fz="sm">
                        Name
                      </Text>
                      <ActionIcon
                        variant="subtle"
                        color={sortBy === GROUP_LIST_ADMIN_SORT_BY.NAME ? 'primary' : 'gray'}
                        onClick={() => handleSort(GROUP_LIST_ADMIN_SORT_BY.NAME)}
                        aria-label="Sort by name"
                      >
                        <Icon name={getSortIconName(GROUP_LIST_ADMIN_SORT_BY.NAME)} size={LAYOUT.SORT_ICON_SIZE} />
                      </ActionIcon>
                    </Group>
                  </Table.Th>
                  <Table.Th>
                    <Group gap={4} className={styles.sortableHeader}>
                      <Text fw={700} fz="sm">
                        Email
                      </Text>
                      <ActionIcon
                        variant="subtle"
                        color={sortBy === GROUP_LIST_ADMIN_SORT_BY.EMAIL ? 'primary' : 'gray'}
                        onClick={() => handleSort(GROUP_LIST_ADMIN_SORT_BY.EMAIL)}
                        aria-label="Sort by email"
                      >
                        <Icon name={getSortIconName(GROUP_LIST_ADMIN_SORT_BY.EMAIL)} size={LAYOUT.SORT_ICON_SIZE} />
                      </ActionIcon>
                    </Group>
                  </Table.Th>
                  <Table.Th>Groups</Table.Th>
                  <Table.Th>Lists</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {paginatedAdmins.length === 0 ? (
                  <Table.Tr>
                    <Table.Td colSpan={4}>
                      <Text ta="center" c="dimmed">
                        No group &amp; list admins found
                      </Text>
                    </Table.Td>
                  </Table.Tr>
                ) : (
                  paginatedAdmins.map((admin) => (
                    <Table.Tr key={admin.userId || admin.email}>
                      <Table.Td>
                        <Text className={styles.adminName}>{getAdminDisplayName(admin)}</Text>
                      </Table.Td>
                      <Table.Td>
                        <Text className={styles.adminEmail}>{admin.email || 'N/A'}</Text>
                      </Table.Td>
                      <Table.Td>
                        {admin.groups.length > 0 ? (
                          <Group gap="xs">
                            {admin.groups.map((group) => (
                              <RemovableScopeChip
                                key={group.id}
                                label={group.name}
                                isRemoving={
                                  removingKey ===
                                  getRemovingKey(admin.userId, GROUP_LIST_ADMIN_SCOPE_TYPE.GROUP, group.id)
                                }
                                onRemove={() => handleRemoveGroupClick(admin, group.id, group.name)}
                              />
                            ))}
                          </Group>
                        ) : (
                          <Text className={styles.emptyCell}>N/A</Text>
                        )}
                      </Table.Td>
                      <Table.Td>
                        {admin.lists.length > 0 ? (
                          <Group gap="xs">
                            {admin.lists.map((list) => (
                              <RemovableScopeChip
                                key={list.id}
                                label={list.name}
                                isRemoving={
                                  removingKey ===
                                  getRemovingKey(admin.userId, GROUP_LIST_ADMIN_SCOPE_TYPE.LIST, list.id)
                                }
                                onRemove={() => handleRemoveListClick(admin, list.id, list.name)}
                              />
                            ))}
                          </Group>
                        ) : (
                          <Text className={styles.emptyCell}>N/A</Text>
                        )}
                      </Table.Td>
                    </Table.Tr>
                  ))
                )}
              </Table.Tbody>
            </Table>

            {sortedAdmins.length > LAYOUT.PAGE_SIZE && (
              <Group justify="center" className={styles.paginationRow}>
                <Pagination total={totalPages} value={page} onChange={setPage} />
              </Group>
            )}
          </>
        )}

        <Modal
          opened={Boolean(pendingRemoval)}
          onClose={() => setPendingRemoval(null)}
          title={`Remove ${scopeTypeLabel} access?`}
          centered
        >
          <Stack mt="md">
            <Text>
              Remove {pendingRemoval?.scopeName} from {pendingRemoval?.userName}&apos;s {scopeTypeLabel} access?
            </Text>
            <Group justify="flex-end">
              <Button variant="default" onClick={() => setPendingRemoval(null)} disabled={Boolean(removingKey)}>
                Cancel
              </Button>
              <Button color="red" onClick={handleConfirmRemoval} loading={Boolean(removingKey)}>
                Remove
              </Button>
            </Group>
          </Stack>
        </Modal>
      </Stack>
    </Paper>
  );
};
